import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../utils/axios';
import { useAuth } from "../../context/AuthContext/AuthContext";
import { Link } from "react-router-dom";
import Button from '../../components/Button/Button';
import FormElement from '../Forms/Forms';
import { toastError } from '../../utils/toast';
import { getSafeRedirectPath } from '../../utils/redirectUtils';

// Redirects returned by POST /auth/login that represent a mandatory step the
// user must complete before reaching any other page — these always override
// a deep link the user was trying to reach. Kept in sync by hand with the
// server's redirect sources: server/helpers/authRedirect.js
// (getRedirectForAuthenticatedUser's forcePasswordChange/onboarding-driver/
// mobile-driver payment-onboarding branches) and the /login route's
// isInReview and isEmailVerified checks in server/routes/authentication.js.
// mandatoryRedirectSync.test.js fails if this list drifts from the server.
//
// '/payment-methods' is checked by BASE PATH below (see redirectBasePath) —
// the server actually returns '/payment-methods?mode=onboarding' (PP-612),
// and today that specific branch is native-only (gated on the `x-client:
// mobile` header, which this web client never sends), so it can't currently
// reach this component at all. Listed anyway so the classification is
// correct if that ever changes, and so mandatoryRedirectSync.test.js has
// something to check it against.
export const MANDATORY_LOGIN_REDIRECTS = ['/password-reset', '/onboarding/email', '/onboarding/driver', '/payment-methods', '/in-review'];

const LoginForm = ({ handleShowReset, onSuccessCallback }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [keepLoggedIn, setKeepLoggedIn] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const { setUser } = useAuth();
    const [loginError, setLoginError] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    // A user can arrive here wanting to return to a specific protected page two
    // ways: PrivateWrapper's route guard stashes the blocked route in
    // location.state.from (an SPA navigation, e.g. a deep link into a protected
    // page while logged out), or axios.js's session-expiry interceptor encodes
    // it in a `redirect` query param (a hard page reload, so no router state
    // survives). Check state first — it carries the full location including
    // search/hash — and fall back to the query param.
    const fromLocation = location.state?.from;
    const stateRedirect = fromLocation
        ? getSafeRedirectPath(`${fromLocation.pathname}${fromLocation.search}${fromLocation.hash}`)
        : null;
    const queryParams = new URLSearchParams(location.search);
    const queryRedirect = getSafeRedirectPath(queryParams.get('redirect'));
    const clientRedirect = stateRedirect || queryRedirect;
    let redirect = clientRedirect || '/';

    const handleLoginError = (err) => {
        console.error(err);
        setLoginError(err);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError(null); // Clear any previous login errors

        if (email.trim().length === 0 || password.trim().length === 0) {
            handleLoginError("Email and password cannot be empty.");
            return;
        }

        setIsLoading(true); // start loading

        const requestPromise = axios.post('/auth/login', {
            email: email,
            password: password,
            keepLoggedIn: keepLoggedIn,
        });

        try {
            const response = await requestPromise;
            if (response.status === 200) {
                    setUser(response.data.user);

                if (onSuccessCallback) onSuccessCallback(response); // This could be improved

                // The server's redirect is either a mandatory gate the user must pass
                // through, or just a default landing page for their role. A deep link
                // the user was already trying to reach wins over a default landing
                // page, but never over a mandatory gate. Membership is checked against
                // the base path (query string stripped) — a mandatory gate can carry
                // one (e.g. '/payment-methods?mode=onboarding'), and MANDATORY_LOGIN_REDIRECTS
                // lists bare paths, so comparing the raw value would never match.
                const redirectBasePath = response.data.redirect ? response.data.redirect.split('?')[0] : null;
                if (response.data.redirect && (!clientRedirect || MANDATORY_LOGIN_REDIRECTS.includes(redirectBasePath))) {
                    redirect = response.data.redirect;
                }

                // Conditional to prevent potential navigation looping
                if (redirect && redirect !== '/login' && redirect !== '/register') {
                    navigate(redirect);
                }

            } else {
                toastError(response.msg);
                setIsLoading(false); // stop loading
            }
        } catch (err) {
            console.error("Login Error", err);
            const errorMessage = err?.response?.data?.error || err?.response?.data?.msg || 'Incorrect email or password. Please try again.';
            handleLoginError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm text-left">
                <form className="space-y-6" onSubmit={handleLogin}>
                    <div>
                        <FormElement
                            id="email"
                            name="email"
                            type="email"
                            variant="input"
                            autoComplete="email"
                            label="Email address"
                            showLabel={true}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoFocus
                            required
                        />
                    </div>

                    <div>
                        <FormElement
                            id="password"
                            name="password"
                            type="password"
                            variant="input"
                            autoComplete="current-password"
                            label="Password"
                            showLabel={true}
                            value={password}
                            onChange={(e) =>
                                setPassword(e.target.value)
                            }
                            required
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <FormElement
                                id="keepLoggedIn"
                                name="keepLoggedIn"
                                defaultChecked={keepLoggedIn}
                                label="Keep me logged in for 30 days"
                                showLabel={true}
                                onChange={(e) => setKeepLoggedIn(e.target.checked)}
                                variant="checkbox"
                            />
                        </div>
                        <div className="text-sm">
                            <a
                                onClick={handleShowReset}
                                className="font-semibold text-black hover:text-red-700 cursor-pointer"
                            >
                                Forgot password?
                            </a>
                        </div>
                    </div>

                    {loginError && (
                        <div className="text-red-500 text-sm font-semibold mt-2 text-center">
                            {loginError}
                        </div>
                    )}

                    <div>
                        <Button
                            type="submit"
                            variant="primary"
                            size="normal"
                            className="w-full"
                            loading={isLoading}
                        >
                            Sign in
                        </Button>
                    </div>
                </form>

                <p className="mt-10 text-center text-sm text-gray-500">
                    New to Park Pro?{" "}
                    <Link
                        to="/register"
                        className="font-semibold leading-6 text-black hover:text-red-700"
                    >
                        Get started for free
                    </Link>
                </p>
            </div>
        </>
    );
};

export default LoginForm;